import { useState, useEffect, useMemo, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  collection, collectionGroup, deleteDoc, doc, onSnapshot, query, orderBy,
  where, limit, setDoc, addDoc, updateDoc, serverTimestamp,
} from 'firebase/firestore';
import { getClientDb } from '../firebase/clientApp';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import AppLayout from '../components/AppLayout';
import { IconPlusSm } from '../components/HeaderControls';
import { canAccessPage, COST_MARGIN_KEY } from '../utils/pages';
import { Button, Dropdown, StatCard, Modal, FormField, Input } from '../components/ui';
import { AddReminderModal, REMINDER_TYPE_MAP, fmtTime } from '../components/ReminderModal';
import { useSettings } from '../hooks/useSettings';
import { fmtMoney, CURRENCY_LIST } from '../utils/currency';
import { saleRevenueOf } from '../utils/saleExpenses';
import { saleBrokerRows } from '../utils/saleMoney';
import type { ActivityEntry, BrokerPayment, Entity, Reminder, PurchaseInvoice, SaleInvoice, InventoryItem, InventoryMemo, SupplierMemo, BankAccount, DashboardExchangeRate, TodoTask, TransactionCategory } from '../types';

// Firestore's serverTimestamp() lands as a Timestamp at read time even though
// the app's own types declare createdAt as `string` — this reads either shape
// (Timestamp instance, plain {seconds}, or an ISO string) into sortable millis.
function toMillis(v: unknown): number {
  if (!v) return 0;
  if (typeof v === 'string') return new Date(v).getTime();
  const obj = v as { toMillis?: () => number; seconds?: number };
  if (typeof obj.toMillis === 'function') return obj.toMillis();
  if (typeof obj.seconds === 'number') return obj.seconds * 1000;
  return 0;
}

// The Dashboard deliberately always shows the "Real" view (no toggle here,
// unlike Purchase/Inventory/Sales) — an invoice/item qualifies as long as it
// has at least one non-books-dummy line, same "any line qualifies" rule
// those list pages already use for their own Real filter.
function isRealInvoice(lineItems?: { category?: TransactionCategory }[]): boolean {
  return (lineItems ?? []).some(li => (li.category ?? 'books-real') !== 'books-dummy');
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const now  = new Date();
const hour = now.getHours();
const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';

// Local calendar day (not UTC), see CLAUDE.md "Date-only strings".
function todayISO() { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; }

// Fixed-order categorical palette (validated for CVD-safe adjacent contrast in
// both themes — see dataviz skill). Assigned by row rank, not by company
// identity, since the top-N list is recomputed fresh per period anyway.
const SERIES_LIGHT = ['#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4', '#008300', '#4a3aa7', '#e34948'];
const SERIES_DARK   = ['#3987e5', '#d95926', '#199e70', '#c98500', '#d55181', '#008300', '#9085e9', '#e66767'];

type StatsPeriod = 'week' | 'month' | '3months' | 'year';
function computeStatsRange(period: StatsPeriod): { start: string; end: string } {
  const toDateStr = (d: Date) => d.toISOString().slice(0, 10);
  const tomorrow = toDateStr(new Date(now.getTime() + 86_400_000));
  switch (period) {
    case 'week': {
      const s = new Date(now); s.setDate(now.getDate() - 6);
      return { start: toDateStr(s), end: tomorrow };
    }
    case 'month':
      return { start: toDateStr(new Date(now.getFullYear(), now.getMonth(), 1)), end: toDateStr(new Date(now.getFullYear(), now.getMonth() + 1, 1)) };
    case '3months':
      return { start: toDateStr(new Date(now.getFullYear(), now.getMonth() - 2, 1)), end: toDateStr(new Date(now.getFullYear(), now.getMonth() + 1, 1)) };
    case 'year': {
      const s = new Date(now); s.setFullYear(now.getFullYear() - 1); s.setDate(s.getDate() + 1);
      return { start: toDateStr(s), end: tomorrow };
    }
  }
}

function outstandingOf(inv: { totalAmountUSD: number; paidUSD?: number; creditedUSD?: number }) {
  return Math.max(0, inv.totalAmountUSD - (inv.paidUSD ?? 0) - (inv.creditedUSD ?? 0));
}

// ── Dashboard section customization — same shape as this app's "Customize
// Columns" feature elsewhere (Inventory.tsx/Purchase.tsx/Sales.tsx): a
// static ordered list decoupled from rendering, persisted as a flat
// localStorage blob merged over an all-visible default key-by-key (so a
// removed section is silently dropped and a newly added one defaults to
// visible). Personal per-browser preference, never BusinessSettings —
// a dashboard layout must never sync across every user of the business. ──

type SectionKey =
  | 'exchangeRates' | 'reminders' | 'todo' | 'dueInvoices' | 'salesPerformance' | 'topClients'
  | 'recentActivity' | 'brokerCommission' | 'cashSnapshot';

interface SectionDef { key: SectionKey; label: string }

const SECTIONS: SectionDef[] = [
  { key: 'exchangeRates',    label: 'Exchange Rates' },
  { key: 'reminders',        label: "Today's Reminders" },
  { key: 'todo',             label: 'To-Do Tasks' },
  { key: 'dueInvoices',      label: 'Due Invoices' },
  { key: 'salesPerformance', label: 'Sales Performance' },
  { key: 'topClients',       label: 'Top Clients' },
  { key: 'recentActivity',   label: 'Recent Activity' },
  { key: 'brokerCommission', label: 'Broker Commission Due' },
  { key: 'cashSnapshot',     label: 'Cash / Bank Balance' },
];

// A small round badge with a company's initials, in a colour picked from
// its name (the same company always gets the same colour).
const AVATAR_HUES = ['#2a78d6', '#eb6834', '#1baf7a', '#c98500', '#d55181', '#4a3aa7', '#0891b2', '#e34948'];
function CompanyAvatar({ name, size = 22 }: { name: string; size?: number }) {
  const clean = (name || '?').trim();
  let h = 0;
  for (const ch of clean) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  const hue = AVATAR_HUES[h % AVATAR_HUES.length];
  const initials = clean.split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0]!.toUpperCase()).join('') || '?';
  return (
    <span aria-hidden="true" style={{
      width: size, height: size, borderRadius: '50%', flexShrink: 0,
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      fontSize: size * 0.42, fontWeight: 800, letterSpacing: '0.02em',
      color: hue, background: `color-mix(in srgb, ${hue} 16%, transparent)`,
      border: `1px solid color-mix(in srgb, ${hue} 32%, transparent)`,
    }}>{initials}</span>
  );
}

// To-Do and Today's Reminders live in the fixed right column: always
// shown, never in Customize (the first two columns are the customisable ones).
const SIDE_SECTIONS: SectionKey[] = ['todo', 'reminders'];

// A card shows only when the user can open at least one of its pages
// (staff see just what they've been given). No entry = everyone.
const SECTION_ACCESS: Partial<Record<SectionKey, string[]>> = {
  reminders:        ['reminders'],
  dueInvoices:      ['ap', 'ar'],
  salesPerformance: ['sales'],
  topClients:       ['sales'],
  recentActivity:   ['activity-log', 'purchase', 'sales', 'inventory-merge'],
  brokerCommission: ['brokers'],
  cashSnapshot:     ['bank'],
};

const DASHBOARD_SECTIONS_KEY = 'dashboard-visible-sections';
const DEFAULT_VISIBLE_SECTIONS: Record<SectionKey, boolean> =
  Object.fromEntries(SECTIONS.map(s => [s.key, true])) as Record<SectionKey, boolean>;

function loadVisibleSections(): Record<SectionKey, boolean> {
  try {
    const raw = localStorage.getItem(DASHBOARD_SECTIONS_KEY);
    if (!raw) return { ...DEFAULT_VISIBLE_SECTIONS };
    const saved = JSON.parse(raw) as Partial<Record<SectionKey, boolean>>;
    const merged = { ...DEFAULT_VISIBLE_SECTIONS };
    for (const s of SECTIONS) if (typeof saved[s.key] === 'boolean') merged[s.key] = saved[s.key]!;
    return merged;
  } catch { return { ...DEFAULT_VISIBLE_SECTIONS }; }
}

// ── Section order — same personal, per-browser localStorage pattern as
// visibility above, just an ordered key array instead of a boolean map.
// Reordering (via the up/down arrows in the Customize dropdown) always
// moves a key within this full list, regardless of that section's current
// visibility, so hiding/reshowing a section never loses its place.
const SECTION_ORDER_KEY = 'dashboard-section-order';
const DEFAULT_SECTION_ORDER: SectionKey[] = SECTIONS.map(s => s.key);

function loadSectionOrder(): SectionKey[] {
  try {
    const raw = localStorage.getItem(SECTION_ORDER_KEY);
    if (!raw) return [...DEFAULT_SECTION_ORDER];
    const saved = JSON.parse(raw) as SectionKey[];
    // Keep only keys that still exist, in their saved order, then append any
    // section not yet in the saved list (newly added since last visit) at
    // the end — same "never trust the saved blob wholesale" rule as visibility.
    const known = new Set(DEFAULT_SECTION_ORDER);
    const kept = saved.filter(k => known.has(k));
    const missing = DEFAULT_SECTION_ORDER.filter(k => !kept.includes(k));
    return [...kept, ...missing];
  } catch { return [...DEFAULT_SECTION_ORDER]; }
}

// ── Icons ─────────────────────────────────────────────────────────────────────

function IconPlus() {
  return <svg width="13" height="13" viewBox="0 0 14 14" fill="none"><path d="M7 2v10M2 7h10" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /></svg>;
}
function IconEdit() {
  return <svg width="11" height="11" viewBox="0 0 14 14" fill="none"><path d="M9.5 2.5l2 2-7 7H2.5v-2l7-7z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" /></svg>;
}
function IconBell({ size = 16 }: { size?: number }) {
  return <svg width={size} height={size} viewBox="0 0 22 22" fill="none">
    <path d="M11 3c-3 0-5 2.2-5 5.5v3L4.5 14.5h13L16 11.5v-3C16 5.2 14 3 11 3z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
    <path d="M9 17.5a2 2 0 004 0" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>;
}
function IconX() {
  return <svg width="11" height="11" viewBox="0 0 14 14" fill="none"><path d="M3 3l8 8M11 3l-8 8" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" /></svg>;
}
function IconInvoiceDue({ size = 16 }: { size?: number }) {
  return <svg width={size} height={size} viewBox="0 0 22 22" fill="none">
    <rect x="4" y="2.5" width="14" height="17" rx="2" stroke="currentColor" strokeWidth="1.5" />
    <path d="M7.5 7.5h7M7.5 11h7M7.5 14.5h4.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
  </svg>;
}
function IconChartBar({ size = 16 }: { size?: number }) {
  return <svg width={size} height={size} viewBox="0 0 22 22" fill="none">
    <path d="M3 19V9M9 19V4M15 19v-6M21 19H1" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>;
}
function IconAlert({ size = 16 }: { size?: number }) {
  return <svg width={size} height={size} viewBox="0 0 22 22" fill="none">
    <path d="M11 2.5l9.5 16.5H1.5L11 2.5z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
    <path d="M11 9v4.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    <circle cx="11" cy="16" r="1" fill="currentColor" />
  </svg>;
}
function IconActivity({ size = 16 }: { size?: number }) {
  return <svg width={size} height={size} viewBox="0 0 22 22" fill="none">
    <path d="M1 11h4l2.5-7 5 14 2.5-7H21" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>;
}
function IconArrowUp() {
  return <svg width="13" height="13" viewBox="0 0 14 14" fill="none"><path d="M7 11V3M3.5 6.5L7 3l3.5 3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>;
}
function IconArrowDown() {
  return <svg width="13" height="13" viewBox="0 0 14 14" fill="none"><path d="M7 3v8M3.5 7.5L7 11l3.5-3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>;
}
function IconHandshake({ size = 16 }: { size?: number }) {
  return <svg width={size} height={size} viewBox="0 0 22 22" fill="none">
    <path d="M2 10l4-4 4 3 3-3 4 4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M2 10l3 6 5 3 5-3 5-6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>;
}
function IconWallet({ size = 16 }: { size?: number }) {
  return <svg width={size} height={size} viewBox="0 0 22 22" fill="none">
    <rect x="2" y="6" width="18" height="13" rx="2" stroke="currentColor" strokeWidth="1.6" />
    <path d="M2 9h18" stroke="currentColor" strokeWidth="1.6" />
    <circle cx="15.5" cy="13.5" r="1.3" fill="currentColor" />
    <path d="M6 6V4.5A1.5 1.5 0 017.5 3h7A1.5 1.5 0 0116 4.5V6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
  </svg>;
}
function IconGem({ size = 16 }: { size?: number }) {
  return <svg width={size} height={size} viewBox="0 0 16 16" fill="none">
    <path d="M4 2h8l3 4-7 8-7-8 3-4z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
    <path d="M1 6h14M6 2 4.5 6 8 14M10 2l1.5 4L8 14" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" />
  </svg>;
}
function IconCustomize() {
  return <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <rect x="1.5" y="2" width="3.2" height="10" rx="1" stroke="currentColor" strokeWidth="1.3" />
    <rect x="5.9" y="2" width="3.2" height="10" rx="1" stroke="currentColor" strokeWidth="1.3" />
    <rect x="10.3" y="2" width="2.2" height="10" rx="1" stroke="currentColor" strokeWidth="1.3" />
  </svg>;
}
function IconArrowOut({ size = 16 }: { size?: number }) {
  return <svg width={size} height={size} viewBox="0 0 22 22" fill="none">
    <path d="M5 17L17 5M17 5H8M17 5V14" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>;
}
function IconExchange({ size = 16 }: { size?: number }) {
  return <svg width={size} height={size} viewBox="0 0 22 22" fill="none">
    <path d="M3 8h13M12.5 4.5 16 8l-3.5 3.5M19 14H6M9.5 10.5 6 14l3.5 3.5"
      stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>;
}
function IconGrip() {
  return <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
    {[3, 9].map(x => [2, 6, 10].map(y => (
      <circle key={`${x}-${y}`} cx={x} cy={y} r="1.15" fill="currentColor" />
    )))}
  </svg>;
}
function IconArrowRight({ size = 13 }: { size?: number }) {
  return <svg width={size} height={size} viewBox="0 0 14 14" fill="none">
    <path d="M2 7h10M8 3l4 4-4 4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>;
}
function IconCalendarDay({ size = 16 }: { size?: number }) {
  return <svg width={size} height={size} viewBox="0 0 22 22" fill="none">
    <rect x="2.5" y="4" width="17" height="15.5" rx="2" stroke="currentColor" strokeWidth="1.5" />
    <path d="M2.5 9h17M6.5 2v4M15.5 2v4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>;
}
function IconChecklist({ size = 16 }: { size?: number }) {
  return <svg width={size} height={size} viewBox="0 0 22 22" fill="none">
    <rect x="2.5" y="2.5" width="17" height="17" rx="3" stroke="currentColor" strokeWidth="1.5" />
    <path d="M6.5 10.5l2 2 4-4.5M6.5 16h6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>;
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function Dashboard() {
  const { user, business } = useAuth();
  const can = (key: string) => canAccessPage(user, key);
  const canCost = can(COST_MARGIN_KEY);
  const sectionAllowed = (key: SectionKey) => !SECTION_ACCESS[key] || SECTION_ACCESS[key]!.some(can);
  const { theme } = useTheme();
  const seriesColors = theme === 'dark' ? SERIES_DARK : SERIES_LIGHT;
  const settings = useSettings();
  const moneyFull = (v: number) => fmtMoney(v ?? 0, settings.currency);

  const [visibleSections, setVisibleSections] = useState<Record<SectionKey, boolean>>(loadVisibleSections);
  useEffect(() => {
    localStorage.setItem(DASHBOARD_SECTIONS_KEY, JSON.stringify(visibleSections));
  }, [visibleSections]);
  function toggleSection(key: SectionKey) {
    setVisibleSections(v => ({ ...v, [key]: !v[key] }));
  }

  const [sectionOrder, setSectionOrder] = useState<SectionKey[]>(loadSectionOrder);
  useEffect(() => {
    localStorage.setItem(SECTION_ORDER_KEY, JSON.stringify(sectionOrder));
  }, [sectionOrder]);
  function resetSections() {
    setVisibleSections({ ...DEFAULT_VISIBLE_SECTIONS });
    setSectionOrder([...DEFAULT_SECTION_ORDER]);
  }

  // ── Responsive column count for the dashboard's card layout below —
  // tracked in JS (not just CSS) because cards are assigned to columns
  // round-robin (see `dashColumns` below), which needs to know the count.
  const [dashCols, setDashCols] = useState<number>(() => {
    if (typeof window === 'undefined') return 3;
    const w = window.innerWidth;
    return w <= 640 ? 1 : 2;
  });
  useEffect(() => {
    function onResize() {
      const w = window.innerWidth;
      setDashCols(w <= 640 ? 1 : 2);
    }
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // ── Drag-and-drop reordering, inside the Customize dropdown only — grab a
  // row's grip handle and drop it on another row to move it there (inserts
  // at the target's position, not just an adjacent swap). The dashboard
  // cards themselves are plain and non-draggable; this dropdown list is the
  // only place order changes.
  const [dragKey, setDragKey] = useState<SectionKey | null>(null);
  const [overKey, setOverKey] = useState<SectionKey | null>(null);

  function handleSectionDrop(targetKey: SectionKey) {
    const from = dragKey;
    setDragKey(null);
    setOverKey(null);
    if (!from || from === targetKey) return;
    setSectionOrder(order => {
      const next = [...order];
      const fromIdx = next.indexOf(from);
      const toIdx = next.indexOf(targetKey);
      if (fromIdx === -1 || toIdx === -1) return order;
      next.splice(fromIdx, 1);
      next.splice(next.indexOf(targetKey), 0, from);
      return next;
    });
  }
  function handleDragEnd() {
    setDragKey(null);
    setOverKey(null);
  }

  // ── Exchange Rates card — up to 3 currency pairs, stored on
  // BusinessSettings (shared across the whole business, unlike the
  // per-browser section-visibility blob above) so the same rates can feed
  // invoice currency conversion later. Owner-editable only, matching how
  // every other BusinessSettings value is managed. ──
  const canEditRates = user?.role === 'owner';
  const exchangeRates = settings.exchangeRates ?? [];
  const [rateModal,   setRateModal]   = useState(false);
  const [draftRates,  setDraftRates]  = useState<DashboardExchangeRate[]>([]);
  const [savingRates, setSavingRates] = useState(false);
  const [rateError,   setRateError]   = useState('');

  // Default pairs offered the first time (before anything's saved):
  // EUR→USD, USD→INR, EUR→INR.
  const DEFAULT_RATE_SLOTS: DashboardExchangeRate[] = [
    { base: 'EUR', quote: 'USD', rate: 0 },
    { base: 'USD', quote: 'INR', rate: 0 },
    { base: 'EUR', quote: 'INR', rate: 0 },
  ];

  function openRateModal() {
    const slots: DashboardExchangeRate[] = [];
    for (let i = 0; i < 3; i++) {
      slots.push(exchangeRates[i] ?? DEFAULT_RATE_SLOTS[i]);
    }
    setDraftRates(slots);
    setRateError('');
    setRateModal(true);
  }
  function updateDraftRate(i: number, patch: Partial<DashboardExchangeRate>) {
    setDraftRates(d => d.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  }
  async function saveRates() {
    const db = getClientDb();
    if (!db || !business) return;
    // A row only needs a base + quote — its rate is optional (0 = show the
    // live market rate automatically). Duplicate/same-currency rows dropped.
    const seen = new Set<string>();
    const cleaned = draftRates
      .map(r => ({ base: r.base, quote: r.quote, rate: Number(r.rate) || 0 }))
      .filter(r => {
        if (!r.base || !r.quote || r.base === r.quote) return false;
        const k = `${r.base}/${r.quote}`;
        if (seen.has(k)) return false;
        seen.add(k);
        return true;
      });
    setSavingRates(true); setRateError('');
    try {
      await setDoc(
        doc(db, 'settings', business.id),
        { exchangeRates: cleaned, updatedAt: serverTimestamp() },
        { merge: true },
      );
      setRateModal(false);
    } catch (e) {
      setRateError(e instanceof Error ? e.message : 'Failed to save. Check permissions for the "settings" collection.');
    }
    setSavingRates(false);
  }

  // ── Live market rates for the configured pairs ───────────────────────────
  // Fetched from a free, key-less FX endpoint so the card fills in on its
  // own; a pair's manually-entered `rate` (if any) takes precedence.
  const ratesKey = exchangeRates.map(r => `${r.base}>${r.quote}`).join(',');
  const [liveRates,   setLiveRates]   = useState<Record<string, number>>({});
  const [ratesAsOf,   setRatesAsOf]   = useState('');
  const [ratesFailed, setRatesFailed] = useState(false);

  useEffect(() => {
    if (!ratesKey) return;   // no pairs configured — nothing to fetch (stale
                             //   liveRates entries are keyed per-pair, so unread)
    const bases = [...new Set(exchangeRates.map(r => r.base))];
    let cancelled = false;
    Promise.all(bases.map(b =>
      fetch(`https://open.er-api.com/v6/latest/${b}`).then(r => r.json()).catch(() => null),
    )).then(results => {
      if (cancelled) return;
      const next: Record<string, number> = {};
      let asOf = '';
      let anyOk = false;
      bases.forEach((b, i) => {
        const data = results[i];
        if (data && data.result === 'success' && data.rates) {
          anyOk = true;
          asOf = data.time_last_update_utc || asOf;
          exchangeRates
            .filter(p => p.base === b)
            .forEach(p => {
              if (typeof data.rates[p.quote] === 'number') next[`${p.base}/${p.quote}`] = data.rates[p.quote];
            });
        }
      });
      setLiveRates(next);
      setRatesAsOf(asOf);
      setRatesFailed(!anyOk);
    });
    return () => { cancelled = true; };
  }, [ratesKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const [entities, setEntities] = useState<Entity[]>([]);

  // Screenshot mode: /dashboard?demo=1 shows a sample name and company in
  // the header instead of the real ones. Nothing is saved.
  const [searchParams] = useSearchParams();
  const demoMode = searchParams.get('demo') === '1';
  const [reminders,       setReminders]       = useState<Reminder[]>([]);
  const [reminderModal,   setReminderModal]   = useState(false);
  const [editingReminder, setEditingReminder] = useState<Reminder | null>(null);

  const [payables,    setPayables]    = useState<PurchaseInvoice[]>([]);
  const [receivables, setReceivables] = useState<SaleInvoice[]>([]);

  const [statsPeriod, setStatsPeriod] = useState<StatsPeriod>('month');
  const [statsSales,  setStatsSales]  = useState<SaleInvoice[]>([]);

  useEffect(() => {
    const db = getClientDb();
    if (!db) return;
    const unsub = onSnapshot(
      query(collection(db, 'entities'), orderBy('createdAt', 'desc')),
      snap => setEntities(snap.docs.map(d => ({ id: d.id, ...d.data() } as Entity))),
      () => {},
    );
    return unsub;
  }, []);

  // ── Reminders — today only ──────────────────────────────────────────────
  // One equality filter only (no orderBy, so no composite index is needed),
  // sorted here. Re-subscribes when the day changes so the card never sticks
  // on yesterday.
  const [reminderDay, setReminderDay] = useState(todayISO);
  useEffect(() => {
    const t = setInterval(() => setReminderDay(d => (todayISO() !== d ? todayISO() : d)), 60_000);
    return () => clearInterval(t);
  }, []);
  useEffect(() => {
    const db = getClientDb();
    if (!db) return;
    const unsub = onSnapshot(
      query(collection(db, 'reminders'), where('date', '==', reminderDay)),
      snap => setReminders(
        snap.docs.map(d => ({ id: d.id, ...d.data() } as Reminder))
          .sort((a, b) => (a.time ?? '').localeCompare(b.time ?? '')),
      ),
      err => console.error('[Dashboard] reminders listener:', err),
    );
    return unsub;
  }, [reminderDay]);

  function openAddReminder()  { setEditingReminder(null); setReminderModal(true); }
  function openEditReminder(r: Reminder) { setEditingReminder(r); setReminderModal(true); }
  function closeReminderModal() { setReminderModal(false); setEditingReminder(null); }

  async function deleteReminder(id: string) {
    const db = getClientDb();
    if (!db) return;
    try { await deleteDoc(doc(db, 'reminders', id)); } catch { /* ignore */ }
  }

  // ── To-Do Tasks — a shared, business-wide checklist (top-level `tasks`
  // collection, same per-tenant-project shape as `reminders` — no
  // businessId filter needed on the query itself, the field is only kept
  // for schema consistency). Incomplete tasks float to the top, newest
  // first within each group. ──
  const [tasks,        setTasks]        = useState<TodoTask[]>([]);
  const [newTaskTitle,  setNewTaskTitle]  = useState('');
  const [addingTask,    setAddingTask]    = useState(false);

  useEffect(() => {
    const db = getClientDb();
    if (!db) return;
    const unsub = onSnapshot(
      collection(db, 'tasks'),
      snap => setTasks(snap.docs.map(d => ({ id: d.id, ...d.data() } as TodoTask))),
      () => {},
    );
    return unsub;
  }, []);

  const sortedTasks = useMemo(() => [...tasks].sort((a, b) => {
    if (a.done !== b.done) return a.done ? 1 : -1;
    return toMillis(b.createdAt) - toMillis(a.createdAt);
  }), [tasks]);
  const openTaskCount = useMemo(() => tasks.filter(t => !t.done).length, [tasks]);

  async function addTask() {
    const title = newTaskTitle.trim();
    const db = getClientDb();
    if (!title || !db || !business || !user) return;
    setAddingTask(true);
    try {
      await addDoc(collection(db, 'tasks'), {
        businessId: business.id, title, done: false,
        createdAt: serverTimestamp(), createdBy: user.id,
      });
      setNewTaskTitle('');
    } catch { /* ignore */ }
    setAddingTask(false);
  }
  async function toggleTask(t: TodoTask) {
    const db = getClientDb();
    if (!db) return;
    try {
      await updateDoc(doc(db, 'tasks', t.id), {
        done: !t.done, doneAt: !t.done ? new Date().toISOString() : null,
      });
    } catch { /* ignore */ }
  }
  async function deleteTask(id: string) {
    const db = getClientDb();
    if (!db) return;
    try { await deleteDoc(doc(db, 'tasks', id)); } catch { /* ignore */ }
  }
  function scrollToTasks() {
    document.getElementById('dash-todo-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  // ── Due invoices — Payable + Receivable ─────────────────────────────────
  useEffect(() => {
    const db = getClientDb();
    if (!db) return;
    const u1 = onSnapshot(
      query(collection(db, 'invoices'), where('paymentStatus', 'in', ['unpaid', 'partial'])),
      snap => setPayables(snap.docs.map(d => ({ id: d.id, ...d.data() } as PurchaseInvoice))),
      () => {},
    );
    const u2 = onSnapshot(
      query(collection(db, 'sales'), where('paymentStatus', 'in', ['unpaid', 'partial'])),
      snap => setReceivables(snap.docs.map(d => ({ id: d.id, ...d.data() } as SaleInvoice))),
      () => {},
    );
    return () => { u1(); u2(); };
  }, []);

  const realPayables    = useMemo(() => payables.filter(inv => isRealInvoice(inv.lineItems)), [payables]);
  const realReceivables = useMemo(() => receivables.filter(inv => isRealInvoice(inv.lineItems)), [receivables]);
  const topPayables    = useMemo(() => [...realPayables].sort((a, b) => a.dueDate.localeCompare(b.dueDate)).slice(0, 4), [realPayables]);
  const topReceivables = useMemo(() => [...realReceivables].sort((a, b) => a.dueDate.localeCompare(b.dueDate)).slice(0, 4), [realReceivables]);
  const overduePayableCount    = useMemo(() => realPayables.filter(i => i.dueDate < todayISO()).length, [realPayables]);
  const overdueReceivableCount = useMemo(() => realReceivables.filter(i => i.dueDate < todayISO()).length, [realReceivables]);

  // ── Needs Attention — memo returns overdue + KYC pending/expiring ───────
  // Two sources: legacy on-memo items (old single-memo fields, pre-dating
  // the per-item InventoryMemo subcollection) and current-model active
  // memos (collection-group query across every item's memos subcollection —
  // no filter/orderBy, so no composite index is needed; filtered client-side).
  const [legacyMemoItems, setLegacyMemoItems] = useState<InventoryItem[]>([]);
  const [activeMemos,     setActiveMemos]     = useState<InventoryMemo[]>([]);

  useEffect(() => {
    const db = getClientDb();
    if (!db) return;
    const u1 = onSnapshot(
      query(collection(db, 'inventory'), where('status', '==', 'on-memo')),
      snap => setLegacyMemoItems(snap.docs.map(d => ({ id: d.id, ...d.data() } as InventoryItem))),
      () => {},
    );
    const u2 = onSnapshot(
      collectionGroup(db, 'memos'),
      snap => setActiveMemos(snap.docs.map(d => ({ id: d.id, ...d.data() } as InventoryMemo)).filter(m => m.status === 'active')),
      () => {},
    );
    return () => { u1(); u2(); };
  }, []);

  const overdueMemoCount = useMemo(() => {
    const legacy = legacyMemoItems.filter(i => i.memoReturnDate && i.memoReturnDate < todayISO()).length;
    const active = activeMemos.filter(m => m.returnByDate && m.returnByDate < todayISO()).length;
    return legacy + active;
  }, [legacyMemoItems, activeMemos]);

  const kycAttentionCount = useMemo(() => {
    const in30 = new Date(now.getTime() + 30 * 86_400_000).toISOString().slice(0, 10);
    return entities.filter(e => e.kycStatus === 'pending' || (!!e.kycExpiryDate && e.kycExpiryDate <= in30)).length;
  }, [entities]);

  // ── Recent Activity — merges Purchase + Sale invoices by creation time ──
  const [recentPurchaseInvoices, setRecentPurchaseInvoices] = useState<PurchaseInvoice[]>([]);
  const [recentSaleInvoices,     setRecentSaleInvoices]     = useState<SaleInvoice[]>([]);

  useEffect(() => {
    const db = getClientDb();
    if (!db) return;
    // Fetches more than the 4 finally shown — the list is now filtered down
    // to "Real" invoices client-side, so a wider pool avoids running dry
    // when several of the most recent docs happen to be books-dummy ones.
    const u1 = onSnapshot(
      query(collection(db, 'invoices'), orderBy('createdAt', 'desc'), limit(20)),
      snap => setRecentPurchaseInvoices(snap.docs.map(d => ({ id: d.id, ...d.data() } as PurchaseInvoice))),
      () => {},
    );
    const u2 = onSnapshot(
      query(collection(db, 'sales'), orderBy('createdAt', 'desc'), limit(20)),
      snap => setRecentSaleInvoices(snap.docs.map(d => ({ id: d.id, ...d.data() } as SaleInvoice))),
      () => {},
    );
    return () => { u1(); u2(); };
  }, []);

  const recentActivity = useMemo(() => {
    const rows = [
      ...recentPurchaseInvoices.filter(inv => isRealInvoice(inv.lineItems)).map(inv => ({
        id: 'p_' + inv.id, kind: 'purchase' as const, ref: inv.invoiceNo || 'Purchase',
        amount: inv.totalAmountUSD ?? 0, when: inv.createdAt, entityId: inv.fromEntityId,
      })),
      ...recentSaleInvoices.filter(inv => isRealInvoice(inv.lineItems)).map(inv => ({
        id: 's_' + inv.id, kind: 'sale' as const, ref: inv.invoiceNo || 'Sale',
        amount: inv.totalAmountUSD ?? 0, when: inv.createdAt, entityId: inv.toEntityId,
      })),
    ];
    return rows.sort((a, b) => toMillis(b.when) - toMillis(a.when)).slice(0, 5);
  }, [recentPurchaseInvoices, recentSaleInvoices]);

  // ── Recent Activity from the Activity Log: who did what, newest first.
  // Only lines for pages this user can open. Falls back to the invoice list
  // above while the log is still empty (older businesses).
  const [logRows, setLogRows] = useState<ActivityEntry[]>([]);
  useEffect(() => {
    const db = getClientDb();
    if (!db) return;
    return onSnapshot(
      query(collection(db, 'activityLog'), orderBy('at', 'desc'), limit(20)),
      snap => setLogRows(snap.docs.map(d => ({ id: d.id, ...d.data() } as ActivityEntry))),
      () => {},
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [business]);
  const LOG_PAGE: Record<ActivityEntry['module'], string> = {
    purchase: 'purchase', sale: 'sales', 'split-merge': 'inventory-merge', data: 'activity-log',
    receipt: 'ar', payment: 'ap', broker: 'brokers', bank: 'bank', entity: 'entity',
    revaluation: 'stock-revaluation',
  };
  const recentLog = useMemo(
    () => logRows.filter(r => can('activity-log') || can(LOG_PAGE[r.module])).slice(0, 5),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [logRows, user],
  );

  // ── Top Clients (goods sold this period, ranked by entity) ──────────────
  const statsRange = useMemo(() => computeStatsRange(statsPeriod), [statsPeriod]);

  useEffect(() => {
    const db = getClientDb();
    if (!db) return;
    const unsub = onSnapshot(
      query(
        collection(db, 'sales'),
        where('saleDate', '>=', statsRange.start),
        where('saleDate', '<',  statsRange.end),
      ),
      snap => setStatsSales(snap.docs.map(d => ({ id: d.id, ...d.data() } as SaleInvoice))),
      () => {},
    );
    return unsub;
  }, [statsRange.start, statsRange.end]);

  function entityName(id: string) { return entities.find(e => e.id === id)?.companyName ?? '—'; }

  const goodsSoldStats = useMemo(() => {
    const realStatsSales = statsSales.filter(inv => isRealInvoice(inv.lineItems));
    const total = realStatsSales.reduce((s, inv) => s + saleRevenueOf(inv), 0);
    const byCompany = new Map<string, number>();
    realStatsSales.forEach(inv => {
      byCompany.set(inv.toEntityId, (byCompany.get(inv.toEntityId) ?? 0) + saleRevenueOf(inv));
    });
    const rows = [...byCompany.entries()]
      .map(([id, amount]) => ({ id, name: entityName(id), amount, pct: total > 0 ? (amount / total) * 100 : 0 }))
      .sort((a, b) => b.amount - a.amount)
      .slice(0, 4);
    return { total, rows };
  }, [statsSales, entities]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Sales Performance — revenue trend, trailing 6 calendar months ───────
  const [trendSales, setTrendSales] = useState<SaleInvoice[]>([]);
  useEffect(() => {
    const db = getClientDb();
    if (!db) return;
    const sixMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 5, 1);
    const unsub = onSnapshot(
      query(collection(db, 'sales'), where('saleDate', '>=', sixMonthsAgo.toISOString().slice(0, 10))),
      snap => setTrendSales(snap.docs.map(d => ({ id: d.id, ...d.data() } as SaleInvoice))),
      () => {},
    );
    return unsub;
  }, []);
  const realTrendSales = useMemo(() => trendSales.filter(inv => isRealInvoice(inv.lineItems)), [trendSales]);

  const [hoveredMonth, setHoveredMonth] = useState<string | null>(null);
  const monthlyTrend = useMemo(() => {
    const months: { key: string; label: string; amount: number }[] = [];
    for (let i = 5; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      months.push({
        key: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`,
        label: d.toLocaleDateString('en-US', { month: 'short' }),
        amount: 0,
      });
    }
    const byMonth = new Map(months.map(m => [m.key, m]));
    realTrendSales.forEach(inv => {
      const m = byMonth.get((inv.saleDate ?? '').slice(0, 7));
      if (m) m.amount += saleRevenueOf(inv);
    });
    const max = Math.max(1, ...months.map(m => m.amount));
    const best = months.reduce((b, m) => (m.amount > b.amount ? m : b), months[0]);
    return { months, max, bestKey: best.amount > 0 ? best.key : null };
  }, [realTrendSales]);

  // ── Broker Commission Due: what each broker is still owed, per invoice.
  // Brokers sit on the lines (Broker 1 / 2), which never update the sale's
  // own brokerPaymentStatus, so read like the Brokers page does: commission
  // per broker from the lines (+ an old header broker), minus that broker's
  // payouts, on sales AND purchases of the last 2 years. ──
  const [brokerSales, setBrokerSales] = useState<SaleInvoice[]>([]);
  const [brokerBuys, setBrokerBuys] = useState<PurchaseInvoice[]>([]);
  const [brokerPays, setBrokerPays] = useState<BrokerPayment[]>([]);
  useEffect(() => {
    const db = getClientDb();
    if (!db) return;
    const t = new Date(); t.setFullYear(t.getFullYear() - 2);
    const from = `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`;
    const u1 = onSnapshot(query(collection(db, 'sales'), where('saleDate', '>=', from)),
      snap => setBrokerSales(snap.docs.map(d => ({ id: d.id, ...d.data() } as SaleInvoice))), () => {});
    const u2 = onSnapshot(query(collection(db, 'invoices'), where('purchaseDate', '>=', from)),
      snap => setBrokerBuys(snap.docs.map(d => ({ id: d.id, ...d.data() } as PurchaseInvoice))), () => {});
    const u3 = onSnapshot(collectionGroup(db, 'brokerPayments'),
      snap => setBrokerPays(snap.docs.map(d => ({ id: d.id, ...d.data() } as BrokerPayment))), () => {});
    return () => { u1(); u2(); u3(); };
  }, []);
  type BrokerOwed = { key: string; brokerId: string; owed: number; dueDate: string };
  const topBrokerDue = useMemo<BrokerOwed[]>(() => {
    // Paid per invoice + broker; an old payment with no broker belongs to the header broker.
    const paid = new Map<string, number>();
    for (const p of brokerPays) {
      const inv = p.saleId ?? p.purchaseId;
      if (!inv) continue;
      const k = `${inv}|${p.brokerEntityId ?? '#header'}`;
      paid.set(k, (paid.get(k) ?? 0) + (p.amount ?? 0));
    }
    const out: BrokerOwed[] = [];
    const scan = (inv: SaleInvoice) => {
      for (const r of saleBrokerRows(inv)) {
        const k = `${inv.id}|${r.line ? r.brokerEntityId : '#header'}`;
        const owed = Math.round((r.commission - (paid.get(k) ?? 0)) * 100) / 100;
        if (owed > 0.005) out.push({ key: `${k}|${r.brokerEntityId}`, brokerId: r.brokerEntityId, owed, dueDate: inv.dueDate ?? '' });
      }
    };
    brokerSales.forEach(scan);
    // A purchase carries the same broker fields as a sale.
    brokerBuys.forEach(b => scan(b as unknown as SaleInvoice));
    // One list, the 4 owed amounts with the earliest due dates.
    return out.sort((a, b) => (a.dueDate || '9999').localeCompare(b.dueDate || '9999')).slice(0, 4);
  }, [brokerSales, brokerBuys, brokerPays]);

  // ── Cash / Bank Balance Snapshot ─────────────────────────────────────────
  const [bankAccounts, setBankAccounts] = useState<BankAccount[]>([]);
  useEffect(() => {
    const db = getClientDb();
    if (!db) return;
    const unsub = onSnapshot(
      collection(db, 'bankAccounts'),
      snap => setBankAccounts(
        snap.docs.map(d => ({ id: d.id, ...d.data() } as BankAccount))
          .filter(a => a.isActive !== false)
          .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)),
      ),
      () => {},
    );
    return unsub;
  }, []);

  // ── Inventory on hand — value of currently in-stock stock, for the top
  // KPI row only. Own snapshot (nothing else here needs inventory). ──
  const [inventoryOnHand, setInventoryOnHand] = useState<InventoryItem[]>([]);
  useEffect(() => {
    const db = getClientDb();
    if (!db) return;
    const unsub = onSnapshot(
      query(collection(db, 'inventory'), where('status', '==', 'in-stock')),
      snap => setInventoryOnHand(snap.docs.map(d => ({ id: d.id, ...d.data() } as InventoryItem))),
      () => {},
    );
    return unsub;
  }, []);

  // ── Supplier memos (Memo In) — consigned, not-yet-owned stock never
  // creates an inventory doc, so its KPI tile sources straight from the
  // memo records themselves (same idea as Memo Out reading activeMemos
  // instead of filtering inventory by a provenance flag). ──
  const [supplierMemos, setSupplierMemos] = useState<SupplierMemo[]>([]);
  useEffect(() => {
    const db = getClientDb();
    if (!db || !business) return;
    const unsub = onSnapshot(
      collection(db, 'supplierMemos'),
      snap => setSupplierMemos(snap.docs.map(d => ({ id: d.id, ...d.data() } as SupplierMemo))),
      () => {},
    );
    return unsub;
  }, [business]);

  // ── Top KPI row — four headline figures, same StatCard tiles the Finance
  // pages use. Sales-this-month reuses trendSales (already loaded for the
  // 6-month chart). AR and Cash/Bank were dropped from this row — both
  // already have their own dedicated dash-grid card (Due Invoices'
  // Receivable side, and Cash / Bank Balance) further down, so showing them
  // again up top was pure duplication. Memo Out reuses data already loaded
  // for the "Needs Attention" card (activeMemos, legacyMemoItems); Memo In
  // reads its own supplierMemos snapshot; stock value reads inventoryOnHand. ──
  const kpis = useMemo(() => {
    const thisMonth = now.toISOString().slice(0, 7);
    // Consigned (memo-in) stock is held, not owned — it never counts toward
    // the business's own stock value (it sits in consigned-stock on the
    // Balance Sheet, offset by consignment-liability). Books-dummy stock is
    // excluded too — the Dashboard always shows the "Real" view.
    const owned = inventoryOnHand.filter(i => i.provenance !== 'memo-in' && i.category !== 'books-dummy');
    // In hand only: the part out on memo is the Memo Out figure, not counted twice.
    const stockValue = owned.reduce((s, i) => {
      const memo = Math.max(0, i.memoCommittedCarat ?? 0);
      const share = memo > 1e-9 && Math.abs(i.carat) > 1e-9 ? (i.carat - memo) / i.carat : 1;
      return s + (i.costUSD ?? 0) * share;
    }, 0);
    const salesThisMonth = realTrendSales
      .filter(inv => (inv.saleDate ?? '').slice(0, 7) === thisMonth)
      .reduce((s, inv) => s + saleRevenueOf(inv), 0);

    // Memo Out — stones currently sent to a client on consignment. Current
    // model (partial-carat InventoryMemo docs) values at its own quoted
    // amountUSD; a legacy on-memo item (whole item, pre-dating the
    // subcollection) has no separate memo price, so it's valued at its own
    // costUSD instead — see the Memo (consignment) note for why both
    // sources still need to be merged everywhere memo data is shown.
    const memoOutValue = activeMemos.reduce((s, m) => s + (m.amountUSD ?? 0), 0)
      + legacyMemoItems.reduce((s, i) => s + (i.costUSD ?? 0), 0);
    const memoOutCount = activeMemos.length + legacyMemoItems.length;

    // Memo In — stock a supplier consigned to us before we bought it. Never
    // creates an inventory doc, so this sources straight from each memo's
    // own heldCostUSD/held-line-count (already maintained on every
    // receive/convert/return) rather than filtering inventory.
    const memoInValue = supplierMemos.reduce((s, m) => s + (m.heldCostUSD ?? 0), 0);
    const memoInCount = supplierMemos.reduce((s, m) => s + m.lines.filter(l => l.status === 'held').length, 0);

    // Last month's sales (for the "vs last month" line) and this month's margin.
    const lm = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const lastMonth = `${lm.getFullYear()}-${String(lm.getMonth() + 1).padStart(2, '0')}`;
    const salesLastMonth = realTrendSales
      .filter(inv => (inv.saleDate ?? '').slice(0, 7) === lastMonth)
      .reduce((s, inv) => s + saleRevenueOf(inv), 0);
    const marginThisMonth = realTrendSales
      .filter(inv => (inv.saleDate ?? '').slice(0, 7) === thisMonth)
      .reduce((s, inv) => s + (inv.totalMarginUSD ?? 0), 0);
    // Money still owed to us / by us (real invoices only, like Due Invoices).
    const toCollect = realReceivables.reduce((s, inv) => s + outstandingOf(inv), 0);
    const toPay     = realPayables.reduce((s, inv) => s + outstandingOf(inv), 0);

    return {
      stockValue, salesThisMonth, memoOutValue, memoOutCount,
      memoInValue, memoInCount,
      stockCount: owned.length,
      salesLastMonth, marginThisMonth, toCollect, toPay,
    };
  }, [inventoryOnHand, realTrendSales, activeMemos, legacyMemoItems, supplierMemos, realReceivables, realPayables]);

  // Weekday name here, not the numeric day — the day-of-month number is
  // already shown in the hero's round badge next to this text. Built from
  // two separate toLocaleDateString calls rather than one call with both
  // `weekday` and `month`/`year` options together — Intl has no fixed
  // pattern for that combination without `day` also present, so browsers
  // reorder it unpredictably (seen rendering as "September 2026 Friday"
  // instead of "Friday, September 2026").
  const dateStr = `${now.toLocaleDateString('en-US', { weekday: 'long' })}, ${now.getDate()} ${now.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}`;

  // Every non-KPI section is one equal cell in a single fixed 3-column grid
  // (`.dash-grid`) — hiding/showing a section via Customize just adds or
  // removes a cell; the remaining cards keep the exact same 1/3 width and
  // flow up to fill, so a toggle never resizes a section or opens a gap.
  // Each card carries its own literal `key` (matching its SectionKey), so
  // rendering `sectionOrder.filter(...).map(k => sectionCards[k])` below
  // just works — no cloneElement needed to inject a dynamic key.
  const sectionCards: Record<SectionKey, ReactNode> = {

    // Exchange Rates — up to 3 configured currency pairs, live rates
    // fetched automatically.
    exchangeRates: (
      <div key="exchangeRates" className="card dash-col" data-sec="exchangeRates">
        <div className="card-title" style={{ marginBottom: 8 }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}><IconExchange /> Exchange Rates</span>
          {canEditRates && (
            <button
              type="button"
              onClick={openRateModal}
              style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--primary)', fontWeight: 600, fontSize: 12 }}
            >
              Edit
            </button>
          )}
        </div>
        {exchangeRates.length === 0 ? (
          <div style={{ fontSize: 12, color: 'var(--text-3)', padding: '10px 0' }}>
            No currency pairs set{canEditRates ? ' — click Edit to add up to three.' : '.'}
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              {exchangeRates.map((r, i) => {
                const live = liveRates[`${r.base}/${r.quote}`];
                const shown = r.rate > 0 ? r.rate : live;
                return (
                  <div key={i} style={{
                    flex: '1 1 0', minWidth: 96,
                    background: 'var(--surface-2)', border: '1px solid var(--border)',
                    borderRadius: 'var(--r-md)', padding: '12px 14px',
                    display: 'flex', flexDirection: 'column', gap: 6,
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 700, color: 'var(--text-3)', letterSpacing: '0.02em' }}>
                      <span>{r.base}</span>
                      <span style={{ color: 'var(--primary)', display: 'flex' }}><IconArrowRight size={10} /></span>
                      <span>{r.quote}</span>
                    </div>
                    <div style={{ fontSize: 18, fontWeight: 800, fontFamily: 'var(--font-num)', color: 'var(--text-1)' }}>
                      {shown != null
                        ? shown.toLocaleString('en-US', { maximumFractionDigits: 4 })
                        : <span style={{ color: 'var(--text-3)' }}>{ratesFailed ? '—' : '…'}</span>}
                    </div>
                  </div>
                );
              })}
            </div>
            {ratesAsOf && (
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 10, fontSize: 10.5, color: 'var(--text-3)' }}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--success)', flexShrink: 0 }} />
                Live · {new Date(ratesAsOf).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
              </div>
            )}
          </>
        )}
      </div>
    ),

    // Today's Reminders
    reminders: (
      <div key="reminders" className="card dash-col" data-sec="reminders">
          <div className="card-title">
            <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}><IconBell /> Today's Reminders</span>
            <Button variant="secondary" size="sm" icon={<IconPlus />} onClick={openAddReminder} style={{ marginLeft: 'auto' }}>
              Add Reminder
            </Button>
            <Link to="/reminders" className="dash-viewall" style={{ marginLeft: 0 }}>View all</Link>
          </div>
          {reminders.length === 0 ? (
            <div className="empty-state" style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '20px 0' }}>
              <div className="empty-state-title" style={{ fontSize: 13 }}>No reminders for today</div>
              <div className="empty-state-sub">Add one to get a nudge here.</div>
            </div>
          ) : (
            // Same checkbox-row shape as the To-Do Tasks card right below it
            // (checkbox · label · dismiss) — a reminder has no persisted
            // "done" state (dismissing just deletes it, same as today), so
            // the checkbox is always unchecked and ticking it dismisses.
            // Capped at 8 — "View all" above covers the rest.
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {reminders.slice(0, 8).map(r => {
                const t = REMINDER_TYPE_MAP[r.type ?? 'other'];
                return (
                  <div key={r.id} style={{
                    display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px',
                    border: '1px solid var(--border)', borderLeft: `3px solid ${t.color}`, borderRadius: 'var(--r-sm)',
                    background: `color-mix(in srgb, ${t.color} 6%, var(--surface))`,
                  }}>
                    <input
                      type="checkbox" checked={false} onChange={() => deleteReminder(r.id)}
                      title="Mark done / dismiss"
                      style={{ accentColor: t.color, flexShrink: 0, cursor: 'pointer' }}
                    />
                    <div style={{
                      width: 24, height: 24, borderRadius: 7, flexShrink: 0,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      background: `color-mix(in srgb, ${t.color} 15%, transparent)`, color: t.color,
                    }}>{t.icon}</div>
                    <div style={{ flex: 1, minWidth: 0, cursor: 'pointer' }} onClick={() => openEditReminder(r)} title="Click to edit">
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{
                          fontWeight: 600, fontSize: 12.5, color: 'var(--text-1)',
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}>{r.title}</span>
                        <span style={{ fontSize: 11, color: 'var(--text-3)', fontFamily: 'var(--font-num)', flexShrink: 0 }}>{fmtTime(r.time)}</span>
                      </div>
                      {r.description && (
                        <div style={{
                          fontSize: 11.5, color: 'var(--text-3)', marginTop: 2,
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}>{r.description}</div>
                      )}
                    </div>
                    <button onClick={() => openEditReminder(r)} title="Edit"
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-3)', padding: 4, flexShrink: 0, display: 'flex' }}>
                      <IconEdit />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
    ),

    // To-Do Tasks — quick-add input up top (Enter or the + button commits),
    // then the checklist itself; a done task's title strikes through and
    // sinks below the still-open ones (see sortedTasks above).
    todo: (
      <div key="todo" id="dash-todo-section" className="card dash-col" data-sec="todo">
          <div className="card-title">
            <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}><IconChecklist /> To-Do Tasks</span>
            <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--text-3)', fontWeight: 600 }}>
              {openTaskCount} open
            </span>
          </div>
          <form
            onSubmit={e => { e.preventDefault(); addTask(); }}
            style={{ display: 'flex', gap: 8, marginBottom: 10 }}
          >
            <input
              value={newTaskTitle} onChange={e => setNewTaskTitle(e.target.value)}
              placeholder="Add a task…"
              style={{
                flex: 1, minWidth: 0, height: 32, padding: '0 10px', fontSize: 12.5,
                border: '1.5px solid var(--border)', borderRadius: 'var(--r-sm)',
                background: 'var(--surface-2)', color: 'var(--text-1)', outline: 'none',
              }}
            />
            <Button type="submit" variant="secondary" size="sm" icon={<IconPlus />} loading={addingTask} disabled={!newTaskTitle.trim()}>
              Add
            </Button>
          </form>
          {sortedTasks.length === 0 ? (
            <div className="empty-state" style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '20px 0' }}>
              <div className="empty-state-title" style={{ fontSize: 13 }}>Nothing on the list</div>
              <div className="empty-state-sub">Add a task above to get started.</div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, overflowY: 'auto', maxHeight: 280 }}>
              {sortedTasks.map((t, ti) => {
                // Each open task gets its own colour (left bar + tick); done ones go grey.
                const c = t.done ? 'var(--text-3)' : seriesColors[ti % seriesColors.length];
                return (
                <div key={t.id} style={{
                  display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px',
                  border: '1px solid var(--border)', borderLeft: `3px solid ${c}`, borderRadius: 'var(--r-sm)',
                  background: t.done ? 'var(--surface-2)' : `color-mix(in srgb, ${c} 6%, var(--surface))`, opacity: t.done ? 0.6 : 1,
                }}>
                  <input
                    type="checkbox" checked={t.done} onChange={() => toggleTask(t)}
                    style={{ accentColor: c, flexShrink: 0, cursor: 'pointer' }}
                  />
                  <span style={{
                    flex: 1, minWidth: 0, fontSize: 12.5, fontWeight: 500, color: 'var(--text-1)',
                    textDecoration: t.done ? 'line-through' : 'none',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    {t.title}
                  </span>
                  <button onClick={() => deleteTask(t.id)} title="Delete"
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-3)', padding: 4, flexShrink: 0, display: 'flex' }}>
                    <IconX />
                  </button>
                </div>
                );
              })}
            </div>
          )}
        </div>
    ),

    // Due Invoices — Payable / Receivable stack when the cell is narrow
    dueInvoices: (
      <div key="dueInvoices" className="card dash-col" data-sec="dueInvoices">
          <div className="card-title"><IconInvoiceDue /> Due Invoices</div>
          {/* Payable above Receivable (one under the other), so each row has
              the card's full width and company names aren't cut short. */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14, flex: 1 }}>
            {can('ap') && (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <div style={{ display: 'flex', alignItems: 'center', fontSize: 11, fontWeight: 700, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
                Payable
                <Link to="/ap" className="dash-viewall" style={{ textTransform: 'none', letterSpacing: 0 }}>View all</Link>
              </div>
              {topPayables.length === 0 ? (
                <div style={{ fontSize: 12, color: 'var(--text-3)', flex: 1, display: 'flex', alignItems: 'center' }}>Nothing outstanding.</div>
              ) : (
                <div className="dash-due-list">
                  {topPayables.map(inv => {
                    const owed    = outstandingOf(inv);
                    const overdue = inv.dueDate < todayISO();
                    return (
                      <div key={inv.id} className="dash-due-row">
                        <span style={{ display: 'flex', alignItems: 'center', gap: 7, minWidth: 0, fontWeight: 600 }}>
                          <CompanyAvatar name={entityName(inv.fromEntityId)} />
                          <span title={entityName(inv.fromEntityId)} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{entityName(inv.fromEntityId)}</span>
                        </span>
                        <span style={{ fontFamily: 'var(--font-num)', fontWeight: 700, color: overdue ? 'var(--danger)' : 'var(--text-1)', flexShrink: 0 }}>
                          {moneyFull(owed)}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            )}

            {can('ap') && can('ar') && <div style={{ height: 1, background: 'var(--border)' }} />}

            {can('ar') && (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <div style={{ display: 'flex', alignItems: 'center', fontSize: 11, fontWeight: 700, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
                Receivable
                <Link to="/ar" className="dash-viewall" style={{ textTransform: 'none', letterSpacing: 0 }}>View all</Link>
              </div>
              {topReceivables.length === 0 ? (
                <div style={{ fontSize: 12, color: 'var(--text-3)', flex: 1, display: 'flex', alignItems: 'center' }}>Nothing outstanding.</div>
              ) : (
                <div className="dash-due-list">
                  {topReceivables.map(inv => {
                    const owed    = outstandingOf(inv);
                    const overdue = inv.dueDate < todayISO();
                    return (
                      <div key={inv.id} className="dash-due-row">
                        <span style={{ display: 'flex', alignItems: 'center', gap: 7, minWidth: 0, fontWeight: 600 }}>
                          <CompanyAvatar name={entityName(inv.toEntityId)} />
                          <span title={entityName(inv.toEntityId)} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{entityName(inv.toEntityId)}</span>
                        </span>
                        <span style={{ fontFamily: 'var(--font-num)', fontWeight: 700, color: overdue ? 'var(--danger)' : 'var(--text-1)', flexShrink: 0 }}>
                          {moneyFull(owed)}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            )}
          </div>
        </div>
    ),

    // Sales Performance — revenue by month, trailing 6 months
    salesPerformance: (
      <div key="salesPerformance" className="card dash-col" data-sec="salesPerformance">
          <div className="card-title"><IconChartBar /> Sales Performance</div>
          {/* This month in one line; margin only for people who may see cost. */}
          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', fontSize: 11.5, color: 'var(--text-3)', marginTop: -6, marginBottom: 10 }}>
            <span>This month <strong style={{ color: 'var(--text-1)', fontFamily: 'var(--font-num)' }}>{moneyFull(kpis.salesThisMonth)}</strong></span>
            <span>Last month <strong style={{ color: 'var(--text-1)', fontFamily: 'var(--font-num)' }}>{moneyFull(kpis.salesLastMonth)}</strong></span>
            {canCost && (
              <span>Margin <strong style={{ color: kpis.marginThisMonth >= 0 ? 'var(--success)' : 'var(--danger)', fontFamily: 'var(--font-num)' }}>{moneyFull(kpis.marginThisMonth)}</strong></span>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, height: 280 }}>
            {monthlyTrend.months.map((m, idx) => {
              const isBest = monthlyTrend.bestKey === m.key;
              const isHovered = hoveredMonth === m.key;
              // Bars live in their own plot area (the month name sits under it), and
              // the tallest reaches 88% so "Best" fits above it. Before, the label
              // shared the column with the bar and squeezed the best month shorter.
              const heightPct = Math.max((m.amount / monthlyTrend.max) * 88, m.amount > 0 ? 4 : 1);
              // Oldest -> newest light-to-dark ramp of the one brand hue (never a
              // rainbow — months are an ordered series, not unrelated categories).
              // The best month always reads as the solid, fully-saturated color
              // regardless of where it falls in the ramp.
              const shadePct = 28 + idx * (64 / Math.max(1, monthlyTrend.months.length - 1));
              const barBg = isBest
                ? 'var(--primary)'
                : `color-mix(in srgb, var(--primary) ${shadePct}%, var(--surface-2))`;
              return (
                <div
                  key={m.key}
                  style={{
                    flex: 1, height: '100%', display: 'flex', flexDirection: 'column',
                    alignItems: 'center', gap: 5, minWidth: 0,
                    position: 'relative', cursor: 'default', outline: 'none',
                  }}
                  onMouseEnter={() => setHoveredMonth(m.key)}
                  onMouseLeave={() => setHoveredMonth(null)}
                  onFocus={() => setHoveredMonth(m.key)}
                  onBlur={() => setHoveredMonth(null)}
                  tabIndex={0}
                  role="img"
                  aria-label={`${m.label}: ${fmtMoney(m.amount, settings.currency)}`}
                >
                  <div style={{ flex: 1, minHeight: 0, width: '100%', position: 'relative', display: 'flex', justifyContent: 'center', alignItems: 'flex-end' }}>
                  {isBest && (
                    <span style={{ position: 'absolute', bottom: `calc(${heightPct}% + 3px)`, left: '50%', transform: 'translateX(-50%)',
                      fontSize: 9, fontWeight: 700, color: 'var(--primary)', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                      Best
                    </span>
                  )}
                  {isHovered && (
                    <div style={{
                      position: 'absolute', bottom: `calc(${heightPct}% + 14px)`, left: '50%',
                      transform: 'translateX(-50%)', zIndex: 5, pointerEvents: 'none',
                      background: 'var(--surface)', border: '1px solid var(--border)',
                      borderRadius: 8, boxShadow: '0 4px 16px rgba(0,0,0,0.16)',
                      padding: '6px 10px', whiteSpace: 'nowrap', textAlign: 'center',
                    }}>
                      <div style={{ fontSize: 9, fontWeight: 700, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                        {m.label}
                      </div>
                      <div style={{ fontSize: 13, fontWeight: 800, color: 'var(--text-1)', fontFamily: 'var(--font-num)' }}>
                        {fmtMoney(m.amount, settings.currency)}
                      </div>
                    </div>
                  )}
                  <div style={{
                    width: '100%', maxWidth: 24, height: `${heightPct}%`,
                    borderRadius: '4px 4px 0 0',
                    background: barBg,
                    boxShadow: isHovered ? '0 0 0 3px var(--primary-light)' : 'none',
                    transition: 'height 0.3s ease, box-shadow 0.15s ease',
                  }} />
                  </div>
                  <span style={{ fontSize: 10.5, fontWeight: 600, color: 'var(--text-3)' }}>{m.label}</span>
                </div>
              );
            })}
          </div>
        </div>
    ),

    topClients: (
      <div key="topClients" className="card dash-col" data-sec="topClients">
          {/* Row 1: title on the left, period switch on the right. */}
          <div className="card-title" style={{ marginBottom: 8 }}>
            <IconChartBar /> Top Clients
            <div className="tb-seg dash-seg" role="tablist" aria-label="Period" style={{ marginLeft: 'auto' }}>
              {([
                { key: 'week',    label: '7D' },
                { key: 'month',   label: 'M'  },
                { key: '3months', label: '3M' },
                { key: 'year',    label: '1Y' },
              ] as { key: StatsPeriod; label: string }[]).map(({ key, label }) => (
                <button key={key} type="button" role="tab" aria-selected={statsPeriod === key}
                  className={statsPeriod === key ? 'on' : undefined} onClick={() => setStatsPeriod(key)}>{label}</button>
              ))}
            </div>
          </div>

          {/* Row 2: the period's total. */}
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 20, fontWeight: 800, fontFamily: 'var(--font-num)', color: 'var(--text-1)' }}>
              {moneyFull(goodsSoldStats.total)}
            </span>
            <span style={{ fontSize: 11.5, color: 'var(--text-3)' }}>
              {statsSales.length} invoice{statsSales.length !== 1 ? 's' : ''}
            </span>
          </div>

          {/* One bar per client: name on the left, amount (share) on the
              right, both inside the bar; the tinted fill is the share. */}
          {goodsSoldStats.rows.length === 0 ? (
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, color: 'var(--text-3)' }}>
              No sales in this period.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flex: 1 }}>
              {goodsSoldStats.rows.map((row, i) => {
                const barColor = seriesColors[i % seriesColors.length];
                return (
                  <div key={row.id} className="dash-client-bar" title={`${row.name}: ${moneyFull(row.amount)} (${row.pct.toFixed(1)}%)`}>
                    <div className="dash-client-fill" style={{ width: `${Math.max(row.pct, 2)}%`, background: `color-mix(in srgb, ${barColor} 22%, transparent)`, borderRight: `2px solid ${barColor}` }} />
                    <span className="dash-client-name">{row.name}</span>
                    <span className="dash-client-amt">
                      {moneyFull(row.amount)} <span style={{ color: 'var(--text-3)' }}>({row.pct.toFixed(0)}%)</span>
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
    ),


    // Recent Activity — Purchase + Sale invoices merged by creation time
    recentActivity: (
      <div key="recentActivity" className="card dash-col" data-sec="recentActivity">
          <div className="card-title">
            <IconActivity /> Recent Activity
            {can('activity-log') && (
              <Link to="/activity" style={{ marginLeft: 'auto', fontSize: 11.5, fontWeight: 600, color: 'var(--primary)', textDecoration: 'none' }}>View all</Link>
            )}
          </div>
          {recentLog.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: 1 }}>
              {recentLog.map(r => {
                const tone = r.action === 'delete' || r.action === 'restore' ? 'var(--danger)' : r.action === 'backup' ? 'var(--text-3)' : r.action === 'edit' ? 'var(--primary)' : r.action === 'split' ? 'var(--warning)' : 'var(--success)';
                const verb = r.action === 'create' ? 'added' : r.action === 'edit' ? 'edited' : r.action === 'delete' ? 'deleted' : r.action === 'backup' ? 'downloaded' : r.action === 'restore' ? 'restored' : 'split';
                const what = r.module === 'sale' ? 'Sale' : r.module === 'purchase' ? 'Purchase' : r.module === 'data' ? 'a backup'
                  : r.module === 'receipt' ? 'a receipt for' : r.module === 'payment' ? 'a payment for' : r.module === 'broker' ? 'a broker payout for'
                  : r.module === 'bank' ? 'a bank entry' : r.module === 'entity' ? 'company'
                  : r.module === 'revaluation' ? 'a stock revaluation' : 'Code';
                const no = r.recordNo ? (r.recordNo.match(/^([A-Za-z]+)-\d+-(\d+)$/) ? r.recordNo.replace(/^([A-Za-z]+)-\d+-(\d+)$/, (_m, a, b) => `${a}-${b.slice(-4)}`) : r.recordNo) : '';
                const when = new Date(r.at);
                const sameDay = when.toDateString() === new Date().toDateString();
                const whenText = sameDay
                  ? when.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
                  : when.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
                return (
                  <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', borderRadius: 'var(--r-sm)', background: 'var(--surface-2)' }}>
                    <CompanyAvatar name={r.userName} size={20} />
                    <span style={{ width: 6, height: 6, borderRadius: '50%', background: tone, flexShrink: 0 }} />
                    <span style={{ flex: 1, minWidth: 0, fontSize: 12, color: 'var(--text-2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                      title={`${r.userName} ${verb} ${what} ${r.recordNo ?? ''}`}>
                      <strong style={{ color: 'var(--text-1)', fontWeight: 600 }}>{r.userName.split(' ')[0]}</strong> {verb} {what}{' '}
                      <span style={{ fontFamily: 'var(--font-num)', color: 'var(--text-1)' }}>{no}</span>
                    </span>
                    <span style={{ fontSize: 11, color: 'var(--text-3)', fontFamily: 'var(--font-num)', flexShrink: 0 }}>{whenText}</span>
                  </div>
                );
              })}
            </div>
          ) : recentActivity.length === 0 ? (
            <div style={{ fontSize: 12, color: 'var(--text-3)', flex: 1, display: 'flex', alignItems: 'center' }}>Nothing recorded yet.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: 1 }}>
              {recentActivity.map(row => (
              <div key={row.id} style={{
                display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px',
                borderRadius: 'var(--r-sm)', background: 'var(--surface-2)',
              }}>
                <span style={{
                  width: 22, height: 22, borderRadius: 6, flexShrink: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: row.kind === 'sale' ? '#ECFDF5' : '#EFF6FF',
                  color: row.kind === 'sale' ? '#059669' : '#2563EB',
                }}>{row.kind === 'sale' ? <IconArrowDown /> : <IconArrowUp />}</span>
                <span style={{ flex: 1, minWidth: 0, fontSize: 12, fontWeight: 600, color: 'var(--text-1)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {row.kind === 'sale' ? 'Sale to' : 'Purchase from'} {entityName(row.entityId)}
                </span>
                <span style={{
                  fontFamily: 'var(--font-num)', fontWeight: 700, fontSize: 12, flexShrink: 0,
                  color: row.kind === 'sale' ? 'var(--success)' : 'var(--text-1)',
                }}>
                  {row.kind === 'sale' ? '+' : ''}{moneyFull(row.amount)}
                </span>
              </div>
              ))}
            </div>
          )}
        </div>
    ),

    brokerCommission: (
      <div key="brokerCommission" className="card dash-col" data-sec="brokerCommission">
          <div className="card-title"><IconHandshake /> Broker Commission Due
            <Link to="/brokers" className="dash-viewall">View all</Link>
          </div>
          {topBrokerDue.length === 0 ? (
            <div style={{ fontSize: 12, color: 'var(--text-3)', flex: 1, display: 'flex', alignItems: 'center' }}>Nothing outstanding.</div>
          ) : (() => {
            // Same look as Due Invoices: one box, overdue amounts in red.
            const row = (inv: BrokerOwed, overdue: boolean) => {
              const owed = inv.owed;
              const name = entityName(inv.brokerId);
              return (
                <div key={inv.key} className="dash-due-row">
                  <span style={{ display: 'flex', alignItems: 'center', gap: 7, minWidth: 0, fontWeight: 600 }}>
                    <CompanyAvatar name={name} />
                    <span title={name} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</span>
                  </span>
                  <span style={{ fontFamily: 'var(--font-num)', fontWeight: 700, color: overdue ? 'var(--danger)' : 'var(--text-1)', flexShrink: 0 }}>
                    {moneyFull(owed)}
                  </span>
                </div>
              );
            };
            const today = todayISO();
            return <div className="dash-due-list">{topBrokerDue.map(inv => row(inv, !!inv.dueDate && inv.dueDate < today))}</div>;
          })()}
        </div>
    ),

    cashSnapshot: (
      <div key="cashSnapshot" className="card dash-col" data-sec="cashSnapshot">
          <div className="card-title"><IconWallet /> Cash / Bank Balance
            <Link to="/bank" className="dash-viewall">View all</Link>
          </div>
          {bankAccounts.length === 0 ? (
            <div style={{ fontSize: 12, color: 'var(--text-3)', flex: 1, display: 'flex', alignItems: 'center' }}>No bank accounts yet.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flex: 1 }}>
              {bankAccounts.map(acc => (
                <div key={acc.id} style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
                  padding: '7px 10px', background: 'var(--surface-2)', borderRadius: 'var(--r-sm)', fontSize: 12,
                }}>
                  <span style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {acc.name}
                  </span>
                  <span style={{ fontFamily: 'var(--font-num)', fontWeight: 700, color: 'var(--text-1)', flexShrink: 0 }}>
                    {fmtMoney(acc.currentBalance ?? 0, acc.currency)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
    ),
  };

  const customizeDropdown = (
    <Dropdown
      align="right"
      closeOnInnerClick={false}
      trigger={() => (
        <Button variant="secondary" size="sm" icon={<IconCustomize />} style={{ height: 34, boxSizing: 'border-box' }}>Customize</Button>
      )}
    >
      <div className="dropdown-group-label">Show &amp; Reorder Sections</div>
      {sectionOrder.filter(k => !SIDE_SECTIONS.includes(k) && sectionAllowed(k)).map(key => {
        const s = SECTIONS.find(sec => sec.key === key)!;
        const isDragging = dragKey === key;
        const isOver = overKey === key && dragKey !== null && dragKey !== key;
        return (
          <div
            key={key}
            className="dropdown-item"
            style={{
              display: 'flex', alignItems: 'center', gap: 6, cursor: 'default',
              opacity: isDragging ? 0.4 : 1,
              background: isOver ? 'var(--surface-2)' : undefined,
              boxShadow: isOver ? 'inset 0 2px 0 var(--primary)' : undefined,
            }}
            onDragOver={e => { e.preventDefault(); setOverKey(key); }}
            onDrop={e => { e.preventDefault(); handleSectionDrop(key); }}
          >
            <button
              type="button"
              className="dash-reorder-handle"
              draggable
              onDragStart={() => setDragKey(key)}
              onDragEnd={handleDragEnd}
              title="Drag to reorder"
              aria-label={`Drag ${s.label} to reorder`}
              style={{
                background: 'none', border: 'none', padding: 3, flexShrink: 0,
                display: 'flex', color: 'var(--text-3)', cursor: 'grab',
              }}
            >
              <IconGrip />
            </button>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={visibleSections[key]}
                onChange={() => toggleSection(key)}
                style={{ accentColor: 'var(--primary)', flexShrink: 0 }}
              />
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.label}</span>
            </label>
          </div>
        );
      })}
      <div className="dropdown-divider" />
      <button type="button" className="dropdown-item" onClick={resetSections}>
        Reset to Default
      </button>
    </Dropdown>
  );

  return (
    <AppLayout
      title={`${greeting}, ${demoMode ? 'Alex' : user?.name?.split(' ')[0] ?? ''}`}
      toolbar={
        <div className="dash-hello-meta">
          {/* Plain text on two lines: company, then today's date. */}
          {demoMode
            ? <span className="dash-hello-company">Brilliant Gems Trading LLC</span>
            : business?.businessName && <span className="dash-hello-company">{business.businessName}</span>}
          <span className="dash-hello-date">{dateStr}</span>
        </div>
      }
      headerActions={<>
        {can('reminders') && (
          <Link to="/reminders" className="dash-icon-link" title="Reminders" aria-label="Reminders"><IconCalendarDay /></Link>
        )}
        <Button variant="secondary" size="sm" onClick={scrollToTasks} style={{ height: 34, boxSizing: 'border-box' }}>
          My Tasks{openTaskCount > 0 ? ` (${openTaskCount})` : ''}
        </Button>
        {customizeDropdown}
        {/* Quick actions: each opens that page's Add form (?new=1). */}
        {(() => {
          const items = [
            { page: 'sales',    to: '/sales?new=1',            label: 'New Sale' },
            { page: 'purchase', to: '/purchase?new=1',         label: 'New Purchase' },
            { page: 'memos',    to: '/memos?new=1',            label: 'Send On Memo' },
            { page: 'memo-in',  to: '/purchase/memo-in?new=1', label: 'Receive Consignment' },
            { page: 'expenses', to: '/accounting/expenses?new=1', label: 'Add Expense' },
          ].filter(i => can(i.page));
          if (items.length === 0) return null;
          return (
            <Dropdown
              align="right"
              trigger={() => (
                <Button variant="primary" size="sm" icon={<IconPlusSm />} style={{ height: 34, boxSizing: 'border-box' }}>New</Button>
              )}
            >
              {items.map(i => <Link key={i.page} to={i.to} className="dropdown-item" style={{ textDecoration: 'none' }}>{i.label}</Link>)}
            </Dropdown>
          );
        })()}
      </>}
    >

      {/* ── Needs attention strip: only when something is overdue, each
          pill opens the page (pages this user can open only). ── */}
      {(() => {
        const pills = [
          { page: 'ar',     to: '/ar',     n: overdueReceivableCount, label: `overdue receivable${overdueReceivableCount === 1 ? '' : 's'}`, tone: 'var(--danger)' },
          { page: 'ap',     to: '/ap',     n: overduePayableCount,    label: `overdue payable${overduePayableCount === 1 ? '' : 's'}`,     tone: 'var(--danger)' },
          { page: 'memos',  to: '/memos',  n: overdueMemoCount,       label: `memo${overdueMemoCount === 1 ? '' : 's'} past return date`, tone: 'var(--warning)' },
          { page: 'entity', to: '/entity', n: kycAttentionCount,      label: 'KYC to check',                                                tone: 'var(--warning)' },
        ].filter(p => p.n > 0 && can(p.page));
        if (pills.length === 0) return null;
        return (
          <div className="dash-attention no-print">
            <span className="dash-attention-label"><IconAlert /> Needs attention</span>
            {pills.map(p => (
              <Link key={p.page} to={p.to} className="dash-attention-pill" style={{ ['--tone' as string]: p.tone }}>
                <strong>{p.n}</strong> {p.label}
              </Link>
            ))}
          </div>
        );
      })()}

      {/* ── Top figures: each opens its page; only pages this user can open.
          Stock value is a cost figure, so it needs the cost right. ── */}
      {(() => {
        const diff = kpis.salesLastMonth > 0 ? ((kpis.salesThisMonth - kpis.salesLastMonth) / kpis.salesLastMonth) * 100 : null;
        const tiles = [
          can('inventory') && { to: '/inventory', node: (
            <StatCard icon={<IconGem />} label="Inventory on Hand" iconColor="#2a78d6" iconBg="color-mix(in srgb, #2a78d6 14%, transparent)"
              value={canCost ? moneyFull(kpis.stockValue) : `${kpis.stockCount} item${kpis.stockCount !== 1 ? 's' : ''}`}
              sub={canCost ? `${kpis.stockCount} in stock item${kpis.stockCount !== 1 ? 's' : ''}` : 'in stock'} />
          ) },
          can('sales') && { to: '/sales', node: (
            <StatCard icon={<IconChartBar />} label="Sales This Month" iconColor="#1baf7a" iconBg="color-mix(in srgb, #1baf7a 14%, transparent)" value={moneyFull(kpis.salesThisMonth)}
              sub={diff === null ? now.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
                : `${diff >= 0 ? '▲' : '▼'} ${Math.abs(diff).toFixed(0)}% vs last month`}
              valueColor={undefined} />
          ) },
          can('ar') && { to: '/ar', node: (
            <StatCard icon={<IconArrowDown />} label="To Collect" iconColor="#0891b2" iconBg="color-mix(in srgb, #0891b2 14%, transparent)" value={moneyFull(kpis.toCollect)}
              sub={`${realReceivables.length} open invoice${realReceivables.length !== 1 ? 's' : ''}`} />
          ) },
          can('ap') && { to: '/ap', node: (
            <StatCard icon={<IconArrowUp />} label="To Pay" iconColor="#e34948" iconBg="color-mix(in srgb, #e34948 14%, transparent)" value={moneyFull(kpis.toPay)}
              sub={`${realPayables.length} open invoice${realPayables.length !== 1 ? 's' : ''}`} />
          ) },
          can('memos') && { to: '/memos', node: (
            <StatCard icon={<IconArrowOut />} label="Memo Out" iconColor="#c98500" iconBg="color-mix(in srgb, #c98500 14%, transparent)" value={moneyFull(kpis.memoOutValue)}
              sub={`${kpis.memoOutCount} item${kpis.memoOutCount !== 1 ? 's' : ''} out`} />
          ) },
        ].filter(Boolean) as { to: string; node: ReactNode }[];
        if (tiles.length === 0) return null;
        return (
          <div className="dash-kpis" style={{ ['--n' as string]: tiles.length, ['--n3' as string]: Math.min(tiles.length, 3) }}>
            {tiles.map(t => <Link key={t.to} to={t.to} className="dash-kpi-link" data-kpi={t.to.slice(1)}>{t.node}</Link>)}
          </div>
        );
      })()}

      {/* ── Every non-KPI section in one reorderable list (same Customize
          dropdown as before: drag to move, checkbox to show/hide), laid
          out as independent column stacks instead of CSS grid rows. Cards
          are assigned to columns round-robin in `sectionOrder` order
          (card 1 → col 1, card 2 → col 2, card 3 → col 3, card 4 → col 1,
          …) — the same left-to-right, top-to-bottom reading order as a
          row-major grid, but since each column is its own independent
          flex stack, a short card doesn't leave a gap waiting for a
          taller card elsewhere in its "row" the way a grid row would;
          each column just packs its own cards tight, one after another,
          at their own natural height. ── */}
      {/* Two customisable columns on the left, and a fixed right column with
          To-Do and Today's Reminders that fills the screen height (each list
          scrolls inside its own card). Under 1000px the right column moves
          below; under 640px everything is one column. */}
      <div className="dash-layout">
        <div className="dash-masonry">
          {Array.from({ length: dashCols }, (_, colIdx) => (
            <div className="dash-masonry-col" key={colIdx}>
              {sectionOrder
                .filter(key => !SIDE_SECTIONS.includes(key) && visibleSections[key] && sectionAllowed(key))
                .filter((_, i) => i % dashCols === colIdx)
                .map(key => sectionCards[key])}
            </div>
          ))}
        </div>
        <aside className="dash-side">
          {sectionCards.todo}
          {sectionAllowed('reminders') && sectionCards.reminders}
        </aside>
      </div>

      <AddReminderModal open={reminderModal} onClose={closeReminderModal} editing={editingReminder} />

      <Modal
        open={rateModal}
        onClose={() => setRateModal(false)}
        title="Exchange Rates"
        subtitle="Up to three currency pairs, shown on the dashboard and used for invoice currency conversion"
        size="md"
        footer={
          <>
            <Button variant="secondary" onClick={() => setRateModal(false)} disabled={savingRates}>Cancel</Button>
            <Button onClick={saveRates} loading={savingRates}>Save</Button>
          </>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {draftRates.map((r, i) => (
            <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
              <FormField label={i === 0 ? 'Base' : undefined}>
                <select value={r.base} onChange={e => updateDraftRate(i, { base: e.target.value })}>
                  {CURRENCY_LIST.map(c => <option key={c.code} value={c.code}>{c.code} — {c.name}</option>)}
                </select>
              </FormField>
              <FormField label={i === 0 ? 'Quote' : undefined}>
                <select value={r.quote} onChange={e => updateDraftRate(i, { quote: e.target.value })}>
                  {CURRENCY_LIST.map(c => <option key={c.code} value={c.code}>{c.code} — {c.name}</option>)}
                </select>
              </FormField>
              <FormField label={i === 0 ? 'Rate (optional)' : undefined}>
                <Input
                  type="number"
                  className="no-spinner"
                  min="0"
                  step="0.0001"
                  placeholder="Live"
                  value={r.rate || ''}
                  onChange={e => updateDraftRate(i, { rate: parseFloat(e.target.value) || 0 })}
                />
              </FormField>
            </div>
          ))}
          <div style={{ fontSize: 11.5, color: 'var(--text-3)' }}>
            Pick a <strong>Base</strong> and <strong>Quote</strong> currency for each pair — the live
            market rate is fetched and shown automatically. Enter a <strong>Rate</strong> only to pin a
            fixed value (how many Quote units one Base unit is worth, e.g. Base EUR, Quote USD, Rate 1.16).
          </div>
          {rateError && <div className="msg msg-error">{rateError}</div>}
        </div>
      </Modal>
    </AppLayout>
  );
}
